import { useCallback, useEffect, useState } from 'react';

/**
 * The icon picker's index, loaded the first time a picker opens (KPR-1188,
 * KPE-037 "icons on demand"): the icons' names, labels, categories and search
 * keywords, ~65 KB gzip and growing with each wave of icons, which only the
 * picker reads. It is components/iconPickerIndex.js in a chunk of its own, and
 * nothing in main.js imports that file statically.
 *
 *   const { status, index, retry } = useIconPickerIndex(open);
 *
 * `status` is 'idle' (not asked for yet), 'loading', 'ready' (`index` is set) or
 * 'failed' (offline, or a deploy removed the chunk the old tab asks for;
 * `retry()` asks again, and so does the next open). The module keeps one
 * promise for the page, so two pickers, or an open, a close and an open,
 * make one request; once it has arrived every later open is ready at once.
 *
 * Tests install a synchronous resolver (src/setupTests.js) so a picker draws
 * its catalog on the first render; the async path has a test of its own that
 * removes it.
 */

let loaded = null;
let pending = null;
let resolver = null;
let importer = null;

const importIndex = () => import(/* webpackChunkName: "icon-picker-index" */ '../components/iconPickerIndex');

function load() {
	if (loaded) return Promise.resolve(loaded);
	if (!pending) {
		pending = (importer || importIndex)()
			.then((module) => {
				loaded = module.default;
				return loaded;
			})
			.catch((error) => {
				pending = null;
				throw error;
			});
	}
	return pending;
}

const peek = () => (resolver ? resolver() : loaded);

/** Tests: a synchronous `() => index`, answered before any import; null removes it. */
export function setIconPickerIndexResolver(fn) {
	resolver = typeof fn === 'function' ? fn : null;
}

/** Tests: replace the `import()` of the chunk, `() => Promise<{ default: index }>`; null puts it back. */
export function setIconPickerIndexImporter(fn) {
	importer = typeof fn === 'function' ? fn : null;
}

/** Tests: forget the loaded index and any import on its way. */
export function resetIconPickerIndex() {
	loaded = null;
	pending = null;
}

export default function useIconPickerIndex(enabled) {
	const [state, setState] = useState(() => {
		const index = peek();
		return index ? { status: 'ready', index } : { status: 'idle', index: null };
	});
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (!enabled) return undefined;
		const index = peek();
		if (index) {
			setState(current => (current.index === index ? current : { status: 'ready', index }));
			return undefined;
		}
		let cancelled = false;
		setState({ status: 'loading', index: null });
		load().then(
			(module) => { if (!cancelled) setState({ status: 'ready', index: module }); },
			() => { if (!cancelled) setState({ status: 'failed', index: null }); },
		);
		return () => { cancelled = true; };
	}, [enabled, attempt]);

	const retry = useCallback(() => setAttempt(n => n + 1), []);
	return { status: state.status, index: state.index, retry };
}
